import { useMemo, useCallback } from "react";
import type { FlowSection, Question } from "../types";

export interface ValidationError {
	id: string;
	field: string;
	sectionId: string;
	sectionType: string;
	message: string;
}

interface UseFormValidationOptions {
	formTitle: string;
	sections: FlowSection[];
	questions: Question[];
	maxQuestions?: number;
}

function getSectionLabel(type: string): string {
	const labels: Record<string, string> = {
		rating: "Rating page",
		welcome: "Welcome page",
		"personal-detail": "Personal details page",
		"company-detail": "Company details page",
		consent: "Consent page",
		"thank-you": "Thank you page",
		"negative-feedback": "Negative feedback page",
		"feedback-thank-you": "Feedback thank you page",
	};
	return labels[type] || "Page";
}

export function useFormValidation({ formTitle, sections, questions, maxQuestions = 10 }: UseFormValidationOptions) {
	const validateSectionTitle = useCallback((section: FlowSection): ValidationError | null => {
		const title = section.title;

		// Empty or whitespace-only is invalid
		if (title.trim() === "") {
			return {
				id: `${section.id}-title-empty`,
				field: "title",
				sectionId: section.id,
				sectionType: section.type,
				message: `${getSectionLabel(section.type)} title cannot be empty`,
			};
		}

		// Exceeds max length
		if (title.length > 255) {
			return {
				id: `${section.id}-title-length`,
				field: "title",
				sectionId: section.id,
				sectionType: section.type,
				message: `${getSectionLabel(section.type)} title exceeds 255 characters`,
			};
		}

		return null;
	}, []);

	const validationErrors = useMemo((): ValidationError[] => {
		const errors: ValidationError[] = [];

		// Validate form title
		if (formTitle.trim() === "") {
			errors.push({
				id: "form-title-empty",
				field: "title",
				sectionId: "form-title",
				sectionType: "form",
				message: "Form title cannot be empty",
			});
		} else if (formTitle.length > 255) {
			errors.push({
				id: "form-title-length",
				field: "title",
				sectionId: "form-title",
				sectionType: "form",
				message: "Form title exceeds 255 characters",
			});
		}

		// Validate all section titles and field labels
		for (const section of sections) {
			const titleError = validateSectionTitle(section);
			if (titleError) {
				errors.push(titleError);
			}

			// Validate field labels
			if (section.fields) {
				for (const field of section.fields) {
					if (field.is_visible && field.label.trim() === "") {
						errors.push({
							id: `${section.id}-field-${field.id}-label-empty`,
							field: "field-label",
							sectionId: section.id,
							sectionType: section.type,
							message: `${getSectionLabel(section.type)}: field label is required`,
						});
					}
				}
			}
		}

		// Validate question titles
		for (const question of questions) {
			const title = question.title;

			// Empty or whitespace-only is invalid
			if (title.trim() === "") {
				errors.push({
					id: `${question.id}-title-empty`,
					field: "title",
					sectionId: question.id,
					sectionType: "question",
					message: "Question title cannot be empty",
				});
			} else if (title.length > 255) {
				errors.push({
					id: `${question.id}-title-length`,
					field: "title",
					sectionId: question.id,
					sectionType: "question",
					message: "Question title exceeds 255 characters",
				});
			}

			// Validate option labels cannot be empty
			const optionLabels = [
				{ field: "video_option_label", label: "Video option label", value: question.video_option_label },
				{ field: "audio_option_label", label: "Audio option label", value: question.audio_option_label },
				{ field: "text_option_label", label: "Text option label", value: question.text_option_label },
			];

			for (const opt of optionLabels) {
				if (opt.value !== undefined && opt.value.trim() === "") {
					errors.push({
						id: `${question.id}-${opt.field}-empty`,
						field: opt.field,
						sectionId: question.id,
						sectionType: "question",
						message: `${opt.label} cannot be empty`,
					});
				}
			}
		}

		// Validate question count
		if (questions.length > maxQuestions) {
			errors.push({
				id: "questions-count",
				field: "questions",
				sectionId: "form",
				sectionType: "form",
				message: `You can add a maximum of ${maxQuestions} questions`,
			});
		}

		return errors;
	}, [formTitle, sections, questions, maxQuestions, validateSectionTitle]);

	const hasValidationErrors = validationErrors.length > 0;

	const getFirstErrorSectionId = useCallback((): string | null => {
		if (validationErrors.length === 0) return null;
		return validationErrors[0].sectionId;
	}, [validationErrors]);

	return {
		validationErrors,
		hasValidationErrors,
		getFirstErrorSectionId,
	};
}
